import { QueryFilterField, type QueryFilter } from "@/API";
import SearchCategories from "@/components/SearchCategories";
import SearchCities from "@/components/SearchCities";
import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import {
  DelieryTime,
  resetValueFilterExplorer,
  updateFilterExplorer,
} from "@/store/filterExplorerState";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { useTranslation } from "react-i18next";
import SearchRestaurantName from "@/components/SearchRestaurantName";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";
import { Lock } from "lucide-react";
import { triggerEvent } from "@/lib/utils";
import SelectPromotionsType, {
  PROMOTIONS_TYPE,
} from "@/components/SelectPromotionsType";
import { entitlementsState } from "@/store/entitlementsState";
import SearchCountries from "@/components/SearchCountries";

type Props = {
  children: ReactNode;
  filter: QueryFilter[];
};

const formFilterExplorerSchema = z.object({
  conditionRate: z.string(),
  rateValue: z.number(),
  deliveryType: z.string(),
  deliveryTime: z.string(),
  status: z.string(),
  platform: z.string(),
  minNumberRating: z.string(),
  maxNumberRating: z.string(),
  promotion: z.string(),
  newRestaurants: z.string(),
});

const OPTIONS_CONDITION_RATING = [
  {
    label: "option_condition_rating_all",
    value: "all",
  },
  {
    label: "option_condition_rating_greater_than",
    value: "min",
  },
  {
    label: "option_condition_rating_less_than",
    value: "max",
  },
  {
    label: "option_condition_rating_equal",
    value: "equal",
  },
];

const OPTIONS_PLATFORM = [
  {
    label: "option_platform_all",
    value: "all",
  },
  {
    label: "UberEats",
    value: "UBEREATS",
  },
  {
    label: "Deliveroo",
    value: "DELIVEROO",
  },
];

const OPTIONS_DELIVERY_TIME = [
  {
    label: "option_delivery_time_all",
    value: DelieryTime.NOT_SET,
  },
  {
    label: "option_delivery_time_zero_ten_minutes",
    value: DelieryTime.ZERO_TEN_MINUTES,
  },
  {
    label: "option_delivery_time_ten_twenty_minutes",
    value: DelieryTime.TEN_TWENTY_MINUTES,
  },
  {
    label: "option_delivery_time_twenty_more_minutes",
    value: DelieryTime.TWENTY_MORE_MINUTES,
  },
];

const OPTIONS_DELIVERY = [
  {
    label: "option_delivery_all",
    value: "all",
  },
  {
    label: "option_delivery_delivery",
    value: "delivery",
  },
  {
    label: "option_delivery_takeaway",
    value: "takeaway",
  },
];

const OPTIONS_PROMOTION = [
  {
    label: "option_promotion_all",
    value: "all",
  },
  {
    label: "option_with_promotion",
    value: "with_promotion",
  },
  {
    label: "option_without_promotion",
    value: "without_promotion",
  },
];

const OPTIONS_NEW_RESTAURANTS = [
  {
    label: "option_new_restaurants_all",
    value: "all",
  },
  {
    label: "option_new_restaurants_1_day",
    value: "new_restaurants_1_day",
  },
  {
    label: "option_new_restaurants_7_days",
    value: "new_restaurants_7_days",
  },
  {
    label: "option_new_restaurants_30_days",
    value: "new_restaurants_30_days",
  },
];

export type FormFilterExplorerSchema = z.infer<typeof formFilterExplorerSchema>;

const Filter: FC<Props> = ({ children, filter }) => {
  const { t } = useTranslation();
  const { data: entitlements } = entitlementsState();

  const advancedFiltersActivated = Boolean(
    entitlements?.find(({ name }) => name === "advanced_filters"),
  );

  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(0);
  const [tags, setTags] = useState<string[]>(
    filter
      .filter(
        ({ field, textValue }) =>
          field === QueryFilterField.CATEGORY && textValue,
      )
      .map((it) => it.textValue ?? ""),
  );
  const [cities, setCities] = useState<string[]>(
    filter
      .filter(
        ({ field, textValue }) => field === QueryFilterField.CITY && textValue,
      )
      .map((it) => it.textValue ?? ""),
  );
  const [countries, setCountries] = useState<string[]>(
    filter
      .filter(
        ({ field, textValue }) =>
          field === QueryFilterField.COUNTRY && textValue,
      )
      .map((it) => it.textValue ?? ""),
  );
  const [restaurants, setRestaurants] = useState<string[]>(
    filter
      .filter(
        ({ field, textValue }) => field === QueryFilterField.NAME && textValue,
      )
      .map((it) => it.textValue ?? ""),
  );
  const [promotionsType, setPromotionsTypes] = useState<string[]>(
    filter
      .filter(({ field }) => PROMOTIONS_TYPE.includes(field))
      .map((it) => it.field ?? ""),
  );

  const form = useForm<z.infer<typeof formFilterExplorerSchema>>({
    resolver: zodResolver(formFilterExplorerSchema),
    defaultValues: {
      rateValue: 0,
      conditionRate: "all",
      deliveryType: "",
      deliveryTime: "",
      status: "",
      platform: "",
      minNumberRating: "0",
      maxNumberRating: "0",
    },
  });

  const handleSubmit = (values: z.infer<typeof formFilterExplorerSchema>) => {
    triggerEvent("Filter Applied", {
      ...values,
      tags,

      cities,
      restaurants,
      promotionsType,
      countries,
    });
    updateFilterExplorer({
      ...values,
      tags,
      cities,
      restaurants,
      promotionsType,
      countries,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      triggerEvent("Filter Panel Opened");
    }
  }, [open]);

  return (
    <Sheet
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="position-relative">
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              handleSubmit(form.getValues());
            }}
          >
            <div className="flex justify-between items-center p-[16px]">
              <Text fontSize={24} fontWeight={600}>
                {t("filters")}
              </Text>
              <Button
                type="button"
                variant="secondaryOutlined"
                size="iconRounded"
                onClick={() => setOpen(false)}
              >
                <X />
              </Button>
            </div>

            <hr />

            <div
              className="p-[16px] flex flex-col gap-[16px] overflow-y-auto"
              style={{
                height: "calc(100dvh - 140px)",
              }}
            >
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("categories")}
                </Text>
                <SearchCategories
                  defaultValue={tags}
                  onChange={(value) => setTags(value)}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("platforms")}
                </Text>
                <FormField
                  control={form.control}
                  name="platform"
                  render={({ field }) => (
                    <FormItem className="w-[100%]">
                      <FormControl>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          {...field}
                        >
                          <SelectTrigger className="w-[100%]">
                            <SelectValue placeholder={t("select_platform")} />
                          </SelectTrigger>
                          <SelectContent>
                            {OPTIONS_PLATFORM.map(({ label, value }) => (
                              <SelectItem
                                key={`platform-${value}`}
                                value={value}
                              >
                                {t(label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("cities")}
                </Text>
                <SearchCities
                  isRestricted={!advancedFiltersActivated}
                  defaultValue={cities}
                  onChange={(value) => setCities(value)}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("countries")}
                </Text>
                <SearchCountries
                  isRestricted={!advancedFiltersActivated}
                  value={countries}
                  onChange={(value) => setCountries(value)}
                />
              </div>
              <div className="flex flex-col gap-[8px] overflow-visible">
                <Text fontSize={14} fontWeight={600}>
                  {t("restaurants")}
                </Text>
                <SearchRestaurantName
                  isRestricted={!advancedFiltersActivated}
                  defaultValue={restaurants}
                  onChange={(value) => setRestaurants(value)}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("label_new_restaurants")}
                </Text>
                <FormField
                  control={form.control}
                  name="newRestaurants"
                  render={({ field }) => (
                    <FormItem className="w-[100%]">
                      <FormControl>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          {...field}
                        >
                          <SelectTrigger className="w-[100%]">
                            <SelectValue
                              placeholder={t("select_new_restaurants")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {OPTIONS_NEW_RESTAURANTS.map(({ label, value }) => (
                              <SelectItem
                                key={`new-restaurants-${value}`}
                                value={value}
                              >
                                {t(label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("promotions")}
                </Text>
                <FormField
                  control={form.control}
                  name="promotion"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          onOpenChange={() => {
                            if (!advancedFiltersActivated) {
                              setActionTriggerNeedCore(true);
                            }
                          }}
                          onValueChange={(value) => field.onChange(value)}
                          {...field}
                        >
                          <SelectTrigger
                            className="w-[100%]"
                            isRestricted={!advancedFiltersActivated}
                          >
                            <SelectValue
                              onClick={() => {}}
                              placeholder={t("select_promotion_active")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {OPTIONS_PROMOTION.map(({ label, value }) => (
                              <SelectItem
                                key={`promotion-${value}`}
                                value={value}
                              >
                                {t(label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("type_of_promotions")}
                </Text>
                <SelectPromotionsType
                  isRestricted={!advancedFiltersActivated}
                  defaultValue={promotionsType}
                  onChange={(value) => setPromotionsTypes(value)}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("type_of_delivery")}
                </Text>
                <FormField
                  control={form.control}
                  name="deliveryType"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          onOpenChange={() => {
                            if (!advancedFiltersActivated) {
                              setActionTriggerNeedCore(true);
                            }
                          }}
                          onValueChange={(value) => field.onChange(value)}
                          {...field}
                        >
                          <SelectTrigger
                            className="w-[100%]"
                            isRestricted={!advancedFiltersActivated}
                          >
                            <SelectValue
                              placeholder={t("select_delivery_type")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {OPTIONS_DELIVERY.map(({ label, value }) => (
                              <SelectItem
                                key={`platform-${value}`}
                                value={value}
                              >
                                {t(label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("delivery_time")}
                </Text>
                <FormField
                  control={form.control}
                  name="deliveryTime"
                  render={({ field }) => (
                    <FormItem className="w-[100%]">
                      <FormControl>
                        <Select
                          onOpenChange={() => {
                            if (!advancedFiltersActivated) {
                              setActionTriggerNeedCore(true);
                            }
                          }}
                          onValueChange={(value) => field.onChange(value)}
                          {...field}
                        >
                          <SelectTrigger
                            className="w-[100%]"
                            isRestricted={!advancedFiltersActivated}
                          >
                            <SelectValue
                              placeholder={t("select_delivery_time")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {OPTIONS_DELIVERY_TIME.map(({ label, value }) => (
                              <SelectItem
                                key={`deliveryTime-${value}`}
                                value={value}
                              >
                                {t(label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("rating")}
                </Text>
                <div className="flex items-center gap-[12px]">
                  <FormField
                    control={form.control}
                    name="conditionRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Select
                            onOpenChange={() => {
                              if (!advancedFiltersActivated) {
                                setActionTriggerNeedCore(true);
                              }
                            }}
                            onValueChange={(value) => field.onChange(value)}
                            {...field}
                          >
                            <SelectTrigger
                              className="w-[100%]"
                              isRestricted={!advancedFiltersActivated}
                            >
                              <SelectValue
                                placeholder={t("filter_by_rating")}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {OPTIONS_CONDITION_RATING.map(
                                ({ label, value }) => (
                                  <SelectItem
                                    key={`${label}-${value}`}
                                    value={value}
                                  >
                                    {t(label)}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <Text fontWeight={600} fontSize={16}>
                    {rate / 10}
                  </Text>
                </div>

                <FormField
                  control={form.control}
                  name="rateValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Slider
                          className="mt-[8px]"
                          onValueChange={(e) => {
                            field.onChange(e[0]);
                            setRate(e[0]);
                          }}
                          defaultValue={[1]}
                          value={[field.value]}
                          max={50}
                          step={1}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex justify-between">
                  <Text fontSize={14} fontWeight={400}>
                    0
                  </Text>
                  <Text fontSize={14} fontWeight={400}>
                    1
                  </Text>
                  <Text fontSize={14} fontWeight={400}>
                    2
                  </Text>
                  <Text fontSize={14} fontWeight={400}>
                    3
                  </Text>
                  <Text fontSize={14} fontWeight={400}>
                    4
                  </Text>
                  <Text fontSize={14} fontWeight={400}>
                    5
                  </Text>
                </div>
              </div>
              <div className="flex flex-col gap-[8px]">
                <Text fontSize={14} fontWeight={600}>
                  {t("number_of_reviews")}
                </Text>
                <div className="grid grid-cols-2 gap-[8px]">
                  <div className="flex flex-col gap-[8px]">
                    <Text fontSize={12} fontWeight={400}>
                      {t("minimum")}
                    </Text>
                    <FormField
                      control={form.control}
                      name="minNumberRating"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value.toString()}
                              endIcon={
                                advancedFiltersActivated ? undefined : Lock
                              }
                              onClick={() => {
                                if (!advancedFiltersActivated) {
                                  setActionTriggerNeedCore(true);
                                }
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-[8px]">
                    <Text fontSize={12} fontWeight={400}>
                      {t("maximum")}
                    </Text>
                    <FormField
                      control={form.control}
                      name="maxNumberRating"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value.toString()}
                              endIcon={
                                advancedFiltersActivated ? undefined : Lock
                              }
                              onClick={() => {
                                if (!advancedFiltersActivated) {
                                  setActionTriggerNeedCore(true);
                                }
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="flex justify-end p-[16px] position-static bottom-[0px]">
              <div className="flex gap-[8px]">
                <Button
                  type="button"
                  variant="secondaryOutlined"
                  onClick={() => {
                    setTags([]);
                    setCities([]);
                    setCountries([]);
                    setRestaurants([]);
                    form.reset();
                    resetValueFilterExplorer();
                    setOpen(false);
                    triggerEvent("Filters Cleared");
                  }}
                >
                  {t("clear_filters")}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isDirty &&
                    tags.length === 0 &&
                    cities.length === 0 &&
                    restaurants.length === 0 &&
                    promotionsType.length === 0 &&
                    countries.length === 0
                  }
                  variant="primaryFilled"
                >
                  {t("apply_filters")}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default Filter;
